import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import type { IToolMcpAction, IToolParameterDefinition } from 'features/chat/agent-chat-module/services/gpt-chat.service';

function ParamsTable({ data }: { data: Array<[string, IToolParameterDefinition]> }) {
  const { t } = useTranslation();

  const formatDefaultValue = (value: unknown) =>
    value && typeof value === 'object'
      ? JSON.stringify(value)
      : String(value ?? (t('TRUE') || 'True'));

  if (data.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center text-center text-muted-foreground">
        {t('NO_DATA') || 'No data'}
      </div>
    );
  }

  return (
    <Table className="text-sm">
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead>{t('NAME') || 'Name'}</TableHead>
          <TableHead>{t('DESCRIPTION') || 'Description'}</TableHead>
          <TableHead>{t('TYPE') || 'Type'}</TableHead>
          <TableHead>{t('REQUIRED') || 'Required'}</TableHead>
          <TableHead>{t('DEFAULT_VALUE') || 'Default Value'}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map(([key, param]) => (
          <TableRow key={key}>
            <TableCell className="font-medium">{param.Name || key}</TableCell>
            <TableCell className="text-muted-foreground">{param.Description || '—'}</TableCell>
            <TableCell className="text-muted-foreground">{param.Type || '—'}</TableCell>
            <TableCell className="text-muted-foreground">
              {param.Required ? t('YES') || 'Yes' : t('NO') || 'No'}
            </TableCell>
            <TableCell className="text-muted-foreground">{formatDefaultValue(param.DefaultValue)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function ActionDetailsModal({
  open,
  onOpenChange,
  action,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  action: IToolMcpAction | null;
}) {
  const { t } = useTranslation();

  if (!action) return null;

  const inputParameters = Object.entries(action.InputParameters || {});
  const outputParameters = Object.entries(action.OutputParameters || {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">{action.Name}</DialogTitle>
          <p className="text-sm text-muted-foreground">{action.Description}</p>
        </DialogHeader>

        <Tabs defaultValue="input" className="mt-4 flex flex-1 flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="input">{t('INPUT_PARAMETERS') || 'Input Parameters'}</TabsTrigger>
            <TabsTrigger value="output">{t('OUTPUT_PARAMETERS') || 'Output Parameters'}</TabsTrigger>
          </TabsList>
          <TabsContent value="input" className="mt-4 flex-1 overflow-y-auto">
            <ParamsTable data={inputParameters} />
          </TabsContent>
          <TabsContent value="output" className="mt-4 flex-1 overflow-y-auto">
            <ParamsTable data={outputParameters} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
